import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BookOpen, Users, GraduationCap, ChevronLeft, ChevronRight, Award } from 'lucide-react';
import Button from '@/components/Button';
import { newsContent } from '@/data/news';

// Función para calcular cuántas tarjetas mostrar según el ancho de la pantalla
function getCardsPerView() {
  if (typeof window !== 'undefined') {
    if (window.innerWidth < 640) return 1; // Móvil
    if (window.innerWidth < 1024) return 2; // Tablet
    return 3; // Escritorio
  }
  return 3; // Valor por defecto
};

export default function InformationCarousel({ openModal }) {
  const [hasNewNews, setHasNewNews] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [cardsPerView, setCardsPerView] = useState(() => getCardsPerView());
  const autoPlayInterval = 5000;

  const cards = [
    {
      id: 1,
      icon: <BookOpen className="h-14 w-14 text-blue-900 mb-4 hover:rotate-6 transition-transform" />,
      title: "Noticias",
      description: "Entérate de las últimas noticias y eventos del colegio.",
      action: () => openModal("news"),
      badge: hasNewNews ? "Nueva" : null,
      link: null
    },
    {
      id: 2,
      icon: <Award className="h-14 w-14 text-blue-900 mb-4 hover:rotate-6 transition-transform" />,
      title: "Nuestros Sellos",
      description: "Conoce los principios que guían nuestra labor educativa.",
      action: () => openModal("sellos"),
      badge: null,
      link: null
    },
    {
      id: 3,
      icon: <Users className="h-14 w-14 text-blue-900 mb-4 hover:rotate-6 transition-transform" />,
      title: "Proceso de admisión",
      description: "Pincha aquí para ver los requisitos, el proceso de inscripción y la lista de espera.",
      action: null,
      badge: null,
      link: "/proceso-admision"
    },
    {
      id: 4,
      icon: <GraduationCap className="h-14 w-14 text-blue-900 mb-4 hover:rotate-6 transition-transform" />,
      title: "Servicios y Beneficios",
      description: "Pincha aquí para ver los servicios y beneficios de la escuela.",
      action: () => openModal("servicios"),
      badge: null,
      link: null
    }
  ];

  const totalSlides = useMemo(() => {
    return cards && cards.length > 0 ? Math.ceil(cards.length / cardsPerView) : 0;
  }, [cards, cardsPerView]);

  const nextSlide = useCallback(() => {
    if (totalSlides <= 1) return;
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    if (totalSlides <= 1) return;
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  useEffect(() => {
    const hasNew = newsContent.some(news => news.isNew);
    setHasNewNews(hasNew);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setCardsPerView(getCardsPerView());
      setCurrentSlide(0);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isHovering && totalSlides && totalSlides > 1) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % totalSlides);
      }, autoPlayInterval);
      return () => clearInterval(timer);
    }
  }, [isHovering, autoPlayInterval, totalSlides]);

  const cardWidth = cardsPerView > 0 ? 100 / cardsPerView : 100;

  return (
    <section className="w-full py-20 bg-white">
      <div className="container mx-auto px-6 md:px-8 text-center">
        <h2 className="text-3xl font-bold tracking-tight text-blue-900 mb-6">
          Información Destacada
        </h2>
        
        <div 
          className="relative"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {totalSlides > 1 && (
            <button 
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-4 z-10 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-full p-2 shadow-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Anterior"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}
          
          <div className="overflow-hidden mx-4 md:mx-8">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                <div 
                  key={slideIndex}
                  className="flex flex-shrink-0"
                  style={{ width: '100%' }}
                >
                  {cards.slice(slideIndex * cardsPerView, (slideIndex + 1) * cardsPerView).map((card) => (
                    <div 
                      key={card.id}
                      className="flex-shrink-0 px-2 md:px-4"
                      style={{ width: `${cardWidth}%` }}
                    >
                      <div className="flex flex-col items-center text-center p-4 md:p-8 rounded-xl border border-blue-100 shadow-lg hover:shadow-xl transition-all duration-300 relative h-full bg-white">
                        {card.badge && (
                          <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md z-20 animate-blink-soft">
                            {card.badge}
                          </span>
                        )}
                        {card.icon} 
                        <h3 className="text-xl font-bold mb-3 text-blue-800">{card.title}</h3>
                        <p className="text-base text-blue-700 flex-grow mb-4">
                          {card.description}
                        </p>
                        <Button
                          variant="default"
                          className="mt-auto"
                          onClick={card.action ? card.action : undefined}
                          href={card.link ? card.link : undefined}
                        >
                          Ver más
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
          
          {totalSlides > 1 && (
            <button 
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-4 z-10 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-full p-2 shadow-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Siguiente"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          )}
        </div>
        
        {totalSlides > 1 && (
          <div className="flex justify-center mt-8 space-x-2">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-3 w-3 rounded-full transition-all duration-300 ${currentSlide === index ? 'bg-blue-800 w-6' : 'bg-blue-300'}`}
                aria-label={`Ir a diapositiva ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
